import { bezDiakritiky } from "@/lib/hladanie";
/**
 * Rozobratie odkazu z eshopu na dodávateľa a kód.
 *
 * User pri zakladaní novej položky (nový dekor) vloží adresu produktu
 * a appka z nej predvyplní to fiddly — **kód** a **dodávateľa**. Názov,
 * cenu a rozmery dopíše sám.
 *
 * Od KOLA 169 je kód z adresy len pre kovanie — dosky a hrany majú kód
 * skladaný, takže z adresy idú **polia**, z ktorých sa skladá (dekor,
 * hrúbka, formát; pri Hranipexe skupina, lepidlo a rozmer hrany).
 *
 * Nič sa **nesťahuje**: parsuje sa reťazec. Čítanie HTML eshopov by
 * znamenalo štyri parsery, ktoré sa rozbijú pri prvom redizajne obchodu,
 * a nedali by sa otestovať.
 */

/** Doména → náš kľúč dodávateľa (rovnaké kľúče ako `eshop_sablony`). */
const DOMENY: { domena: string; dodavatel: string }[] = [
  { domena: "hranipex", dodavatel: "hranipex" },
  { domena: "jafholz", dodavatel: "jafholz" },
  { domena: "demos24plus", dodavatel: "demos" },
  { domena: "twd", dodavatel: "twd" },
  { domena: "hafele", dodavatel: "hafele" },
];

/** Query parametre, v ktorých eshopy nosia hľadaný kód. */
const PARAMETRE_KODU = ["q", "keyword", "search", "query", "kod"];

/**
 * Predpony propagačných skupín Hranipexu (`HD 211381`) — zmerané na
 * 32 841 ABS hranách cenníka. Kópia `services/kod_materialu.PREDPONY_HRANIPEX`:
 * s inou predponou server kód hrany nezloží.
 */
export const PREDPONY_HRANIPEX = ["HD", "HU", "HSE", "HL"] as const;

/**
 * Hrúbky ABS hrán v cenníku (mm) — zmerané na tých istých 32 841 hranách.
 * Sprievodca inú hrúbku nedovolí (KOLO 169).
 */
export const HRUBKY_HRAN = ["0.45", "0.5", "0.6", "0.7", "0.8", "1", "1.5", "2", "3"] as const;

/**
 * Číslo artikla Hranipexu kdekoľvek v adrese: `211381X10019200` = skupina
 * (6 znakov, kratšia doplnená `X`), povrch `X1`, lepidlo `0`/`1`, šírka
 * `019` a hrúbka ×100 `200`. Tvar je odvodený z cenníka (stĺpec Číslo
 * artikla) — skutočné adresy eshopu sa overiť nedali, proxy ich blokuje.
 */
const CISLO_ARTIKLA = /(?<![0-9A-Z])(\d{6}|\d{5}X|X\d{5})X\d([01])(\d{3})(\d{3})(?![0-9])/i;
/** Propagačná skupina v adrese: `HD29160`, `HD 211381`, `hd-211381`. */
const SKUPINA = /(?<![0-9A-Z])(HSE|HD|HU|HL)[\s+_-]?(\d{5,6})(?![0-9])/i;
/** Kód kovania Hranipexu (krytky, Riex): `F005036`. */
const KOD_KOVANIA_HRANIPEX = /(?<![0-9A-Z])F\d{6}(?![0-9])/i;
/**
 * Číslo artikla Häfele: v cenníku `354.01.730`, v adrese eshopu bez bodiek
 * (`/cs/product/zaves-…/35401730/`) — KOLO 194.
 */
const ARTIKEL_HAFELE = /(?<![0-9.])(\d{3})\.?(\d{2})\.?(\d{3})(?![0-9.])/;
/** Segment cesty, ktorý je celý číslom artikla (nie slug s číslom). */
const CELY_ARTIKEL_HAFELE = /^(\d{3})\.?(\d{2})\.?(\d{3})$/;
/** Hrúbka dosky v slugu: `18mm`, `18,5mm`. */
const HRUBKA_V_SLUGU = /^(\d{1,2}(?:[.,]\d+)?)mm$/i;
/** Formát dosky v slugu: `2800x2070`, `4100x600`. */
const FORMAT_V_SLUGU = /^(\d{3,4})x(\d{3,4})$/i;

export interface RozobranyOdkaz {
  /** Náš kľúč dodávateľa, alebo `null` pri neznámej doméne. */
  dodavatel: string | null;
  /** Kód z adresy, alebo `null` keď sa nedá odvodiť. */
  kod: string | null;
  /** Dekor zo slugu (`k682` → `K682`). */
  dekor: string | null;
  /** Kód štruktúry, keď segment sedí na katalóg štruktúr. */
  struktura: string | null;
  /** Výrobca, keď segment sedí na zoznam výrobcov z cenníka. */
  vyrobca: string | null;
  /** Zvyšný segment — typicky obchodný názov dekoru (`Alpaca`). */
  nazov: string | null;
  /** Typ dosky podľa slova v adrese (`skarovka`) — KOLO 167. */
  typDosky: string | null;
  /** Drevina či farba z adresy (`buk` → `BUK`). */
  zaklad: string | null;
  /** Kvalita z adresy (`ab` → `AB`, `ab-b` → `AB/B`). */
  povrch: string | null;
  /** Hranipex: predpona propagačnej skupiny (`HD29160` → `HD`) — KOLO 169. */
  predpona: string | null;
  /** Hranipex: číslo propagačnej skupiny — zo skupiny alebo z čísla artikla. */
  cisloSkupiny: string | null;
  /** Hranipex: lepený variant podľa čísla artikla; `null` = nevieme. */
  lepidlo: boolean | null;
  /** Šírka hrany v mm z čísla artikla (`…019…` → `19`). */
  sirkaHrany: string | null;
  /** Hrúbka hrany v mm z čísla artikla (`200` → `2`, `050` → `0.45`). */
  hrubkaHrany: string | null;
  /** Hrúbka dosky zo slugu (`…-18mm-…` → `18`). */
  hrubkaDosky: string | null;
  /** Formát dosky zo slugu (`2800x2070` → dĺžka `2800`, šírka `2070`). */
  formatDlzka: string | null;
  formatSirka: string | null;
}

/**
 * Typ dosky z `GET /cennik/typy-dosiek` — len to, čo potrebuje odkaz.
 * Slová a dreviny drží server; kópia tu by sa rozišla (lekcia KOLA 77).
 */
export interface TypDoskyOdkazu {
  kluc: string;
  nazov: string;
  dekorova: boolean;
  slova_v_odkaze?: string[];
  navrhy_zakladu?: string[];
}

/** Známe hodnoty z cenníka — posielajú sa zvonku, nekopírujú sa sem. */
export interface ZnameHodnoty {
  /** Kódy štruktúr z katalógu (`PD`, `SM`, `ST9`…). */
  struktury?: string[];
  /** Výrobcovia, ktorí v cenníku už sú (`Kronospan`, `Egger`…). */
  vyrobcovia?: string[];
  /** Typy plošných dosiek zo servera (KOLO 167). */
  typyDosiek?: TypDoskyOdkazu[];
}

/**
 * `k682`, `w1100`, `u11026`, `0500` — dekor je písmeno(á) + 3–5 číslic
 * (Egger `U11026`, Pfleiderer `R20326`), alebo 4 číslice.
 */
const DEKOR = /^(?:[a-z]{1,2}\d{3,5}|\d{4})$/;

/** Je to adresa, ktorú je bezpečné dať do `href`? */
export function jeHttpOdkaz(url: string): boolean {
  const t = url.trim().toLowerCase();
  return t.startsWith("http://") || t.startsWith("https://");
}

/**
 * `https://www.hranipex.sk/sk/hladat/?q=HD29160` → hranipex + HD29160.
 *
 * Kód sa berie z query parametra, a keď tam nie je, z **posledného
 * segmentu cesty** (`demos24plus.com/product/507078/` → `507078`).
 */
export function rozoberOdkaz(
  url: string,
  zname: ZnameHodnoty = {},
): RozobranyOdkaz {
  const prazdny: RozobranyOdkaz = {
    dodavatel: null,
    kod: null,
    dekor: null,
    struktura: null,
    vyrobca: null,
    nazov: null,
    typDosky: null,
    zaklad: null,
    povrch: null,
    predpona: null,
    cisloSkupiny: null,
    lepidlo: null,
    sirkaHrany: null,
    hrubkaHrany: null,
    hrubkaDosky: null,
    formatDlzka: null,
    formatSirka: null,
  };
  if (!jeHttpOdkaz(url)) return prazdny;
  let adresa: URL;
  try {
    adresa = new URL(url.trim());
  } catch {
    return prazdny;
  }

  const host = adresa.hostname.toLowerCase();
  const dodavatel =
    DOMENY.find((d) => host.includes(d.domena))?.dodavatel ?? null;

  let kod: string | null = null;
  for (const meno of PARAMETRE_KODU) {
    const hodnota = adresa.searchParams.get(meno);
    if (hodnota?.trim()) {
      kod = hodnota.trim();
      break;
    }
  }
  if (!kod) {
    // Posledný segment cesty; koncové lomítko sa ignoruje
    const segmenty = adresa.pathname.split("/").filter(Boolean);
    const posledny = segmenty[segmenty.length - 1];
    if (posledny && vyzeraAkoKod(decodeURIComponent(posledny))) {
      kod = decodeURIComponent(posledny);
    }
  }

  const hranipex = dodavatel === "hranipex" ? zHranipexu(adresa) : null;
  if (dodavatel === "hafele") return { ...prazdny, dodavatel, ...zHafele(adresa, kod, zname) };
  const slug = zoSlugu(adresa, zname);
  const doska = typZoSlugu(adresa, zname.typyDosiek ?? [], slug.dekor !== null);
  return {
    ...prazdny,
    dodavatel,
    // Kód kovania (`F005036`) je kód aj v ceste so slugom
    kod: hranipex?.kodKovania ?? kod,
    ...slug,
    typDosky: doska.typ,
    zaklad: doska.zaklad,
    povrch: doska.povrch,
    // Doska bez dekoru (škárovka…) nemá za dekorom nič — názov je celý slug
    nazov: slug.nazov ?? doska.nazov,
    ...(hranipex?.hrana ?? {}),
  };
}

/**
 * Kovanie z adresy Häfele (KOLO 194).
 *
 * `hafele.cz/cs/product/zaves-valcovane-…/35401730/?MasterSKU=P-00899152`
 * — číslo artikla je bez bodiek (cenník ho má `354.01.730`, inak by sa
 * nezhodol s cenníkom ani s unikátnosťou kódu), názov je slug **pred**
 * číslom a výrobca je Häfele, keď ho cenník pozná. `MasterSKU` je interné
 * číslo eshopu, nie kód.
 */
function zHafele(
  adresa: URL,
  kodZAdresy: string | null,
  zname: ZnameHodnoty,
): Pick<RozobranyOdkaz, "kod" | "nazov" | "vyrobca"> {
  const segmenty = adresa.pathname
    .split("/")
    .filter(Boolean)
    .map((x) => {
      try {
        return decodeURIComponent(x);
      } catch {
        return x;
      }
    });
  const sBodkami = (m: RegExpExecArray): string => `${m[1]}.${m[2]}.${m[3]}`;
  let kod: string | null = null;
  let idxKodu = -1;
  // Hľadanie (`?q=354.01.730`) — kód z posledného segmentu rieši cesta nižšie
  const zHladania =
    kodZAdresy && !segmenty.includes(kodZAdresy) ? ARTIKEL_HAFELE.exec(kodZAdresy) : null;
  if (zHladania) kod = sBodkami(zHladania);
  for (let i = segmenty.length - 1; i >= 0 && !kod; i--) {
    const m = CELY_ARTIKEL_HAFELE.exec(segmenty[i] ?? "");
    if (m) {
      kod = sBodkami(m);
      idxKodu = i;
    }
  }
  // Slug produktu stojí pred číslom artikla; bez čísla je to posledný segment
  const slug = segmenty[idxKodu >= 0 ? idxKodu - 1 : segmenty.length - 1] ?? "";
  const jeSlug = slug.includes("-") && !/^\d/.test(slug);
  const slova = jeSlug ? slug.split("-").filter(Boolean).join(" ") : "";
  const nazov = slova ? slova.charAt(0).toUpperCase() + slova.slice(1) : null;
  const vyrobca =
    (zname.vyrobcovia ?? []).find((v) => bezDiakritiky(v) === "hafele") ?? null;
  return { kod: kod ?? kodZAdresy, nazov, vyrobca };
}

/** Celá adresa bez domény — čísla môžu byť v ceste aj v query. */
function textAdresy(adresa: URL): string {
  const surova = adresa.pathname + adresa.search;
  try {
    return decodeURIComponent(surova);
  } catch {
    return surova; // rozbité %-kódovanie — hľadá sa aj tak
  }
}

/** Hrúbka z čísla artikla: `200` → `2`; `050` je v cenníku takmer vždy 0,45. */
function hrubkaZArtikla(trojcislie: string): string | null {
  // 0,45 mm je v čísle artikla zaokrúhlená na `050` (5 134 hrán), 0,5 mm
  // len na 106 — dvojznačnosť sa rieši v prospech častejšej
  const hrubka = trojcislie === "050" ? "0.45" : String(Number(trojcislie) / 100);
  return (HRUBKY_HRAN as readonly string[]).includes(hrubka) ? hrubka : null;
}

/**
 * Hrana alebo kovanie z adresy Hranipexu (KOLO 169).
 *
 * Číslo artikla nesie skupinu, lepidlo aj rozmer, ale nie predponu —
 * tú dá skupina (`HD29160`), ak je v adrese, inak ju navrhne cenník
 * (`/navrh-materialu` → `skupina`).
 */
function zHranipexu(adresa: URL): {
  hrana: Partial<RozobranyOdkaz>;
  kodKovania: string | null;
} {
  const text = textAdresy(adresa);
  const hrana: Partial<RozobranyOdkaz> = {};
  const artikel = CISLO_ARTIKLA.exec(text);
  if (artikel) {
    hrana.cisloSkupiny = (artikel[1] ?? "").replace(/x/gi, "");
    hrana.lepidlo = artikel[2] === "1";
    const sirka = Number(artikel[3]);
    if (sirka > 0) hrana.sirkaHrany = String(sirka);
    hrana.hrubkaHrany = hrubkaZArtikla(artikel[4] ?? "");
  }
  const skupina = SKUPINA.exec(text);
  if (skupina) {
    hrana.predpona = (skupina[1] ?? "").toUpperCase();
    hrana.cisloSkupiny = skupina[2] ?? null;
  }
  const kovanie = KOD_KOVANIA_HRANIPEX.exec(text);
  return { hrana, kodKovania: kovanie ? kovanie[0].toUpperCase() : null };
}

/**
 * Je posledný segment cesty kód, alebo ľudský slug?
 *
 * `507078` a `HD29160` kódy sú. `lamino-dtdl-kronospan-k682-pd-alpaca`
 * nie — je to názov produktu rozdelený pomlčkami a do poľa ID nemá čo
 * robiť (presne to sa stalo pri JafHolz adrese). Rozhoduje: obsahuje
 * číslicu, je krátky a nie je to viac slov.
 */
function vyzeraAkoKod(segment: string): boolean {
  // `~p17384082` je interné ID eshopu — slug ostáva slugom
  const holy = segment.split("~")[0] ?? "";
  if (!/\d/.test(holy)) return false;
  if (holy.length > 20) return false;
  return !holy.includes("-") && !holy.includes("_");
}

/**
 * Z posledného segmentu cesty sa dá vytiahnuť viac než kód.
 *
 * `lamino-dtdl-kronospan-k682-pd-alpaca~p17384082` nesie výrobcu,
 * dekor, štruktúru aj obchodný názov. Je to **predvyplnenie** — user to
 * vidí a prepíše; nič sa nesťahuje, parsuje sa reťazec.
 */
function zoSlugu(
  adresa: URL,
  zname: ZnameHodnoty,
): Pick<
  RozobranyOdkaz,
  "dekor" | "struktura" | "vyrobca" | "nazov" | "hrubkaDosky" | "formatDlzka" | "formatSirka"
> {
  const segmenty = adresa.pathname.split("/").filter(Boolean);
  const posledny = segmenty[segmenty.length - 1] ?? "";
  // `~p17384082` je interné ID eshopu, do názvu nepatrí
  const slug = decodeURIComponent(posledny).split("~")[0] ?? "";
  const casti = slug.split("-").filter(Boolean);

  const strukturyMap = new Map(
    (zname.struktury ?? []).map((s) => [s.toLowerCase(), s]),
  );
  const vyrobcoviaMap = new Map(
    (zname.vyrobcovia ?? []).map((v) => [v.toLowerCase(), v]),
  );

  let dekor: string | null = null;
  let struktura: string | null = null;
  let vyrobca: string | null = null;
  let hrubkaDosky: string | null = null;
  let formatDlzka: string | null = null;
  let formatSirka: string | null = null;
  let dekorIdx = -1;
  // Segmenty za dekorom, ktoré si nikto nenárokoval — to je názov
  const zvysok: string[] = [];

  casti.forEach((cast, i) => {
    const maly = cast.toLowerCase();
    // Hrúbka a formát (JafHolz `…-18mm-2800x2070`) do názvu nepatria
    const hrubka = HRUBKA_V_SLUGU.exec(maly);
    if (hrubka) {
      hrubkaDosky ??= (hrubka[1] ?? "").replace(",", ".");
      return;
    }
    const format = FORMAT_V_SLUGU.exec(maly);
    if (format) {
      formatDlzka ??= format[1] ?? null;
      formatSirka ??= format[2] ?? null;
      return;
    }
    if (!vyrobca && vyrobcoviaMap.has(maly)) {
      vyrobca = vyrobcoviaMap.get(maly)!;
      return;
    }
    if (!dekor && DEKOR.test(maly)) {
      dekor = maly.toUpperCase();
      dekorIdx = i;
      return;
    }
    // Štruktúra až po dekore — `PD` je kód štruktúry, ale segment pred
    // dekorom býva typ produktu (`dtdl`), nie povrch
    if (!struktura && dekor && strukturyMap.has(maly)) {
      struktura = strukturyMap.get(maly)!;
      return;
    }
    // Pred dekorom je typ produktu (`lamino`, `dtdl`) — do názvu nejde
    if (dekorIdx >= 0 && i > dekorIdx) zvysok.push(cast);
  });

  const nazov =
    zvysok.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join(" ") || null;

  return { dekor, struktura, vyrobca, nazov, hrubkaDosky, formatDlzka, formatSirka };
}

/** Kvalita dreva v adrese: `ab`, `b`, `bk`, `bbcp` — písmená tried A–D, K, P. */
const KVALITA = /^[a-dkp]{1,4}$/;
/** Kde v názve stála kvalita — doplní sa, keď je celý rad prečítaný. */
const MIESTO_KVALITY = "\u0000kvalita";

interface DoskaZoSlugu {
  typ: string | null;
  zaklad: string | null;
  povrch: string | null;
  nazov: string | null;
}

/**
 * Typ dosky, drevina a kvalita zo slugu (KOLO 167).
 *
 * `skarovka-1-vrstvova-buk-ab--cinkovana-lamela~p15462164` → škárovka,
 * BUK, AB a názov „Škárovka 1 vrstvova buk AB – cinkovana lamela“. Typ
 * sa hľadá najprv v slugu, potom v kategórii cesty — tá však býva
 * spoločná (`skarovky--biodosky-z-…`), preto platí len jednoznačná.
 * Dekorová doska (slug s dekorom) môže byť len dekorový typ a naopak.
 */
function typZoSlugu(
  adresa: URL,
  typy: TypDoskyOdkazu[],
  maDekor: boolean,
): DoskaZoSlugu {
  const nic: DoskaZoSlugu = { typ: null, zaklad: null, povrch: null, nazov: null };
  if (typy.length === 0) return nic;
  const segmenty = adresa.pathname.split("/").filter(Boolean).map((x) => decodeURIComponent(x).toLowerCase());
  const slug = (segmenty[segmenty.length - 1] ?? "").split("~")[0] ?? "";
  const kandidati = (slova: string[]): TypDoskyOdkazu[] =>
    typy.filter(
      (t) => t.dekorova === maDekor && (t.slova_v_odkaze ?? []).some((x) => slova.includes(x)),
    );
  const tokeny = slug.split("-").filter(Boolean);
  let typ = kandidati(tokeny)[0] ?? null;
  for (const segment of segmenty.slice(0, -1).reverse()) {
    if (typ) break;
    const k = kandidati(segment.split("-").filter(Boolean));
    if (k.length === 1) typ = k[0] ?? null;
  }
  if (!typ || typ.dekorova) return { ...nic, typ: typ?.kluc ?? null };

  const dreviny = new Map((typ.navrhy_zakladu ?? []).map((d) => [d.toLowerCase(), d]));
  const slovoTypu = typ.nazov.split(" ")[0] ?? typ.kluc;
  let zaklad: string | null = null;
  // Kvalita = súvislý rad tokenov za drevinou (`ab`, `ab-b` = AB/B)
  const kvalita: string[] = [];
  let kvalitaHotova = false;
  const nazvy = slug.split("--").map((skupina) => {
    const slova: string[] = [];
    for (const token of skupina.split("-").filter(Boolean)) {
      if ((typ.slova_v_odkaze ?? []).includes(token)) {
        slova.push(slovoTypu);
      } else if (zaklad === null && dreviny.has(token)) {
        zaklad = dreviny.get(token) ?? null;
        slova.push(token);
      } else if (zaklad !== null && !kvalitaHotova && KVALITA.test(token)) {
        if (kvalita.length === 0) slova.push(MIESTO_KVALITY);
        kvalita.push(token.toUpperCase());
      } else {
        if (kvalita.length > 0) kvalitaHotova = true;
        slova.push(token);
      }
    }
    if (kvalita.length > 0) kvalitaHotova = true;
    return slova;
  });
  const kvalitaText = kvalita.join("/");
  const nazov = nazvy
    .map((slova) => slova.map((w) => (w === MIESTO_KVALITY ? kvalitaText : w)).join(" "))
    .filter(Boolean)
    .join(" – ");
  return {
    typ: typ.kluc,
    zaklad,
    povrch: kvalita.length ? kvalita.join("/") : null,
    nazov: nazov || null,
  };
}

